import React, { useEffect, useState } from "react";
import { Container, Box, Paper, Typography, Button } from "@mui/material";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import AttachMoneyRounded from "@mui/icons-material/AttachMoneyRounded";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import StarRounded from "@mui/icons-material/StarRounded";
import SpeedRounded from "@mui/icons-material/SpeedRounded";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import usePublicLocale from "../i18n/usePublicLocale";
import PublicPageHeader from "../components/PublicPageHeader";
import PublicFooterNav from "../components/PublicFooterNav";
import PublicBackdrop from "../components/PublicBackdrop";
import Reveal from "../components/ui/Reveal";
import { MediaFrame } from "../components/ui/Marketing";
// import i18n from '../i18n'
import { AVAILABLE_LANGS } from "../i18n/languages";
import {
  setCanonical,
  addHreflangVariants,
  clearHreflang,
  setPageMeta,
  clearMeta,
  summarizeText,
  setStructuredData,
  clearStructuredData,
} from "../utils/head";
import axios from "../api";
import formatCurrencyPretty from "../utils/currency";

const SERVICE_LINKS = {
  agency: {
    intent: "/gielda-zlecen",
    cta: "/cta/broadcaster/book-demo",
  },
  mediadock_cloud: {
    live: "/transmisje-live",
    srt: "/serwer-srt",
    restream: "/restreaming",
  },
  mediadock_storage: {
    archive: "/nagrywanie-transmisji-w-chmurze",
  },
  payperview: {
    ppv: "/pay-per-view-transmisje",
  },
  mediadock_playout: {
    rundown: "/scenariusz-transmisji",
    distribution: "/restreaming",
  },
  web_intercom: {
    intercom: "/interkom-online",
  },
  web_cg: {
    graphics: "/grafika-ekranowa",
  },
  prompter: {
    prompter: "/sufler-online",
  },
  run_of_show: {
    rundown: "/scenariusz-transmisji",
  },
  world_clock: {
    remote: "/produkcja-zdalna",
  },
  broadcast_manager: {
    remote: "/produkcja-zdalna",
    solutions: "/cta/broadcaster/solutions",
  },
  freelancer_marketplace: {
    work: "/praca-dla-freelancerow",
    briefs: "/gielda-zlecen",
    portfolio: "/cta/freelancer/portfolio",
  },
};

const inlineLinkSx = {
  color: "primary.main",
  fontWeight: 600,
  textDecoration: "underline",
  textDecorationColor: "rgba(25, 118, 210, 0.35)",
  textUnderlineOffset: "0.14em",
  "&:hover": {
    textDecorationColor: "currentColor",
  },
};

export default function Services() {
  const theme = useTheme();
  usePublicLocale(["services", "landing", "serviceLinks"]);
  const { t, i18n } = useTranslation("services");
  const [publicPricing, setPublicPricing] = useState(null);

  useEffect(() => {
    try {
      const base = `https://mediadock.tv/services`;
      setCanonical(base);
      addHreflangVariants(
        base,
        AVAILABLE_LANGS.map((l) => l.code),
      );
    } catch (e) {}
    return () => {
      try {
        clearHreflang();
      } catch (e) {}
    };
  }, []);

  useEffect(() => {
    try {
      const isPl = (i18n.language || "").toLowerCase().startsWith("pl");
      const titleBase = t("title", { defaultValue: "Uslugi" });
      const titleSuffix = i18n.t("services_title_suffix", {
        ns: "meta",
        defaultValue: isPl ? "moduły i rozwiązania" : "modules and solutions",
      });
      const title = isPl
        ? `${titleBase} — ${titleSuffix}`
        : `${titleBase} — ${titleSuffix}`;
      const introTitle = t("intro.title", { defaultValue: "" });
      const introBody = t("intro.body", {
        returnObjects: true,
        defaultValue: [],
      });
      const firstBody = Array.isArray(introBody) ? introBody[0] : introBody;
      const lead = i18n.t("services_lead", {
        ns: "meta",
        defaultValue: isPl
          ? "Poznaj usługi MediaDock dla produkcji, emisji i współpracy zespołów."
          : "Explore MediaDock services for production, playout and team collaboration.",
      });
      const description = summarizeText(
        `${lead} ${introTitle} ${firstBody || ""}`.trim(),
      );
      const keywords = t("keywords", { defaultValue: "" });
      const image = "https://mediadock.tv/social/social_bg.png";
      setPageMeta({
        title,
        description,
        keywords,
        image,
        canonical: "https://mediadock.tv/services",
      });
      const pageUrl =
        typeof location !== "undefined"
          ? `${location.origin}${location.pathname || "/"}${location.search || ""}`
          : "https://mediadock.tv/services";
      setStructuredData("services-webpage", {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: title,
        description,
        url: pageUrl,
        inLanguage: i18n.language || "pl",
        isPartOf: {
          "@type": "WebSite",
          name: "MediaDock",
          url: "https://mediadock.tv/",
        },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: image,
        },
      });
      setStructuredData("services-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        inLanguage: i18n.language || "pl",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "MediaDock",
            item: "https://mediadock.tv/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: title,
            item: pageUrl,
          },
        ],
      });
    } catch (e) {}
    return () => {
      try {
        clearMeta();
        clearStructuredData("page");
      } catch (e) {}
    };
  }, [i18n.language]);

  useEffect(() => {
    let mounted = true;
    axios
      .get("/pricing", { params: { _ts: Date.now() }, skipErrorRedirect: true })
      .then((res) => {
        if (mounted) setPublicPricing(res?.data || {});
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const agencyPrice =
    publicPricing &&
    Object.prototype.hasOwnProperty.call(
      publicPricing,
      "price_agency_query_pln",
    )
      ? publicPricing.price_agency_query_pln
      : null;
  const agencyPriceFormatted =
    agencyPrice != null
      ? formatCurrencyPretty(Number(agencyPrice) || 0, "PLN", i18n.language)
      : null;

  const items = [
    "agency",
    "distributor",
    "mediadock_cloud",
    "mediadock_storage",
    "payperview",
    "mediadock_playout",
    "web_intercom",
    "web_cg",
    "prompter",
    "run_of_show",
    "world_clock",
    "broadcast_manager",
    "freelancer_marketplace",
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        color: theme.palette.text.primary,
      }}
    >
      <PublicBackdrop opacity={0.6} />
      <Container sx={{ py: 10, position: "relative", zIndex: 1 }} maxWidth="lg">
        <PublicPageHeader title={t("title")} />

        <Reveal>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 0.9fr) minmax(0, 1.1fr)",
              },
              gap: { xs: 3, md: 5 },
              mb: { xs: 5, md: 8 },
              alignItems: "start",
            }}
          >
            <Paper
              variant="flat"
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: "var(--app-public-card-radius)",
                color: "text.primary",
              }}
            >
              <Typography variant="h3" component="h2" sx={{ mb: 2.5 }}>
                {t("intro.title")}
              </Typography>
              {(
                t("intro.body", { returnObjects: true, defaultValue: [] }) || []
              ).map((para, i) => (
                <Typography key={i} paragraph sx={{ mb: 2 }}>
                  {para}
                </Typography>
              ))}
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("intro.footer")}
              </Typography>
            </Paper>
            <MediaFrame
              sx={{
                height: { xs: 260, md: 420 },
                minHeight: 0,
                alignSelf: "start",
              }}
            >
              <Box
                component="img"
                src="/media/marketing/services-network.webp"
                alt={t("intro.title")}
                loading="eager"
                decoding="async"
                sx={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  minHeight: 0,
                  objectFit: "cover",
                }}
              />
            </MediaFrame>
          </Box>
        </Reveal>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 6, md: 10 },
            mb: 3,
          }}
        >
          {items.map((k, idx) => {
            const title = t(`${k}.title`);
            const body =
              t(`${k}.body`, {
                returnObjects: true,
                ...(k === "agency"
                  ? { price: agencyPriceFormatted || "—" }
                  : {}),
              }) || [];
            const linksForService = SERVICE_LINKS[k];
            const reversed = idx % 2 === 1;
            return (
              <React.Fragment key={k}>
                <Reveal>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: {
                        xs: "column",
                        md: reversed ? "row-reverse" : "row",
                      },
                      gap: { xs: 3, md: 6 },
                      alignItems: "center",
                    }}
                  >
                    {/* image */}
                    <Box
                      sx={{
                        flex: { xs: "0 0 auto", md: "0 0 45%" },
                        minWidth: { xs: "100%", md: 280 },
                      }}
                    >
                      <Box
                        component="img"
                        src={`/cta/${k}.png`}
                        alt={title}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.src = "/cta/service-1.png";
                        }}
                        sx={{
                          width: "100%",
                          height: "auto",
                          borderRadius: "var(--app-public-card-radius)",
                          display: "block",
                        }}
                      />
                    </Box>

                    {/* content */}
                    <Box
                      sx={{
                        ml: 2,
                        flex: "1 1 0",
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        component={RouterLink}
                        to={`/services/${k}`}
                        sx={{
                          mb: 1,
                          textDecoration: "none",
                          display: "inline-flex",
                          alignItems: "center",
                          color: "primary.main",
                        }}
                      >
                        <ChevronRightRounded
                          fontSize="small"
                          sx={{ mr: 1, transform: "translateY(1px)" }}
                        />
                        <Typography
                          variant="h4"
                          component="span"
                          sx={{ m: 0, color: "inherit" }}
                        >
                          {title}
                        </Typography>
                      </Box>

                      {Array.isArray(body) ? (
                        body.slice(0, 2).map((p, i) => (
                          <Typography key={i} paragraph sx={{ mb: 1 }}>
                            {p}
                          </Typography>
                        ))
                      ) : (
                        <Typography paragraph sx={{ mb: 1 }}>
                          {body}
                        </Typography>
                      )}

                      {/* Feature list (5 items) - pulled from i18n if available (PL), fallback to defaults */}
                      <Box
                        component="ul"
                        sx={{
                          listStyle: "none",
                          p: 0,
                          m: 0,
                          mt: 1,
                          ml: 2,
                          mb: 2,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {(() => {
                          const featureIcons = [
                            AttachMoneyRounded,
                            AttachMoneyRounded,
                            TrendingUpRounded,
                            StarRounded,
                            SpeedRounded,
                          ];
                          const features = t(`${k}.features`, {
                            returnObjects: true,
                            defaultValue: [],
                          });
                          const fallback = [
                            "Konkurencyjne ceny",
                            "Elastyczne opcje rozliczeń",
                            "Wysoka efektywność",
                            "Unikalne funkcje",
                            "Prosta integracja",
                          ];
                          const list =
                            Array.isArray(features) && features.length
                              ? features
                              : fallback;
                          return list.map((text, i) => {
                            const Icon = featureIcons[i] || AttachMoneyRounded;
                            return (
                              <Box
                                component="li"
                                key={i}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  ...(i === 1 ? { mb: 2 } : {}),
                                }}
                              >
                                <Icon
                                  fontSize="small"
                                  sx={{ color: "primary.main" }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ color: "text.primary" }}
                                >
                                  {text}
                                </Typography>
                              </Box>
                            );
                          });
                        })()}
                      </Box>

                      {linksForService && (
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 2,
                            color: "text.secondary",
                            lineHeight: 1.65,
                          }}
                        >
                          <Trans
                            ns="serviceLinks"
                            i18nKey={k}
                            components={Object.fromEntries(
                              Object.entries(linksForService).map(
                                ([name, to]) => [
                                  name,
                                  <Box
                                    key={name}
                                    component={RouterLink}
                                    to={to}
                                    sx={inlineLinkSx}
                                  />,
                                ],
                              ),
                            )}
                          />
                        </Typography>
                      )}

                      {Array.isArray(body) &&
                        body.slice(2).map((p, i) => (
                          <Typography
                            key={`rest-${i}`}
                            paragraph
                            sx={{ mb: 1 }}
                          >
                            {p}
                          </Typography>
                        ))}
                    </Box>
                  </Box>
                </Reveal>

                {idx < items.length - 1 && (
                  <Box
                    aria-hidden="true"
                    sx={{
                      mx: "auto",
                      width: { xs: "90%", md: "70%" },
                      height: "1px",
                      bgcolor: "divider",
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Box>
        <PublicFooterNav />
      </Container>
    </Box>
  );
}
